import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Star, MapPin, Calendar, Plus, Trash2, Edit, Send, 
  ExternalLink, CheckCircle, Clock, X, Heart, MessageCircle,
  Newspaper, CalendarDays, Zap, Eye, DollarSign, Link, Table, Play, Image
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContentManager = ({ adminToken }) => {
  const [activeSection, setActiveSection] = useState("niamh");
  const [niamhPosts, setNiamhPosts] = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [niamhSchedule, setNiamhSchedule] = useState({ scheduled_posts: [], upcoming_fridays: [], categories: [] });
  const [senSchedule, setSenSchedule] = useState({ scheduled_posts: [], upcoming_fridays: [], categories: [] });
  const [tickerMessages, setTickerMessages] = useState([]);
  const [venues, setVenues] = useState({ venues: [], stats: {} });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  // NEW: Newsletter and calendar state
  const [contentCalendar, setContentCalendar] = useState({ calendar_items: [], scheduled_newsletters: [] });
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [scheduleView, setScheduleView] = useState("table"); // "table" or "posts"
  const [senScheduleView, setSenScheduleView] = useState("table");
  const [editingScheduledPost, setEditingScheduledPost] = useState(null);
  
  // Form states
  const [newPost, setNewPost] = useState({ title: "", content: "", category: "loving", video_url: "", activity_sheet_url: "", image_url: "", prompt_question: "" });
  const [newScheduledPost, setNewScheduledPost] = useState({ 
    title: "", content: "", category: "loving", scheduled_date: "", scheduled_time: "14:00",
    image_url: "", video_url: "", activity_sheet_url: "", prompt_question: "", 
    include_in_newsletter: true, is_sen_content: false 
  });
  const [newSenPost, setNewSenPost] = useState({
    title: "", content: "", category: "tips", scheduled_date: "", scheduled_time: "14:00",
    image_url: "", video_url: "", external_link: "", prompt_question: "",
    include_in_newsletter: true, tags: []
  });
  const [newTicker, setNewTicker] = useState({ message: "", emoji: "🌟", link: "", priority: 5 });
  const [newVenue, setNewVenue] = useState({ name: "", category: "soft_play", city: "", postcode: "", website: "", email: "", description: "" });
  const [newEvent, setNewEvent] = useState({ name: "", description: "", date: "", time: "", location: "", city: "", website: "", is_free: false });
  const [newCalendarItem, setNewCalendarItem] = useState({ title: "", content_type: "niamh_post", scheduled_date: "", content: "" });
  const [showForm, setShowForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showSenScheduleForm, setShowSenScheduleForm] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  useEffect(() => {
    if (activeSection === "niamh") { fetchNiamhPosts(); fetchPendingComments(); fetchNiamhSchedule(); }
    if (activeSection === "sen") { fetchSenSchedule(); }
    if (activeSection === "ticker") fetchTickerMessages();
    if (activeSection === "venues") fetchVenues();
    if (activeSection === "events") fetchEvents();
    if (activeSection === "newsletter") { fetchContentCalendar(); fetchPricing(); }
    if (activeSection === "affiliates") fetchAffiliateStats();
  }, [activeSection]);

  // Fetch functions
  const fetchNiamhPosts = async () => {
    try {
      const res = await axios.get(`${API}/niamhs-corner/posts?limit=50`);
      setNiamhPosts(res.data.posts || []);
    } catch (error) {
      console.error("Error fetching posts:", error);
    }
  };

  const fetchPendingComments = async () => {
    try {
      const res = await axios.get(`${API}/admin/niamhs-corner/comments?status=pending`, { headers });
      setPendingComments(res.data.comments || []);
    } catch (error) {
      console.error("Error fetching pending comments:", error);
    }
  };

  const moderateComment = async (commentId, status) => {
    try {
      await axios.put(`${API}/admin/niamhs-corner/comments/${commentId}/moderate`, { status }, { headers });
      toast.success(`Comment ${status}!`);
      fetchPendingComments();
    } catch (error) {
      toast.error("Failed to moderate comment");
    }
  };

  const fetchNiamhSchedule = async () => {
    try {
      const res = await axios.get(`${API}/admin/niamhs-corner/schedule`, { headers });
      setNiamhSchedule(res.data);
    } catch (error) {
      console.error("Error fetching schedule:", error);
    }
  };

  const createScheduledPost = async () => {
    if (!newScheduledPost.title || !newScheduledPost.content || !newScheduledPost.scheduled_date) {
      toast.error("Title, content, and date are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/niamhs-corner/schedule`, newScheduledPost, { headers });
      toast.success("Post scheduled!");
      setNewScheduledPost({ 
        title: "", content: "", category: "loving", scheduled_date: "", scheduled_time: "14:00",
        image_url: "", video_url: "", activity_sheet_url: "", prompt_question: "", 
        include_in_newsletter: true, is_sen_content: false 
      });
      setShowScheduleForm(false);
      fetchNiamhSchedule();
    } catch (error) {
      toast.error("Failed to schedule post");
    }
  };

  const updateScheduledPost = async (scheduleId, postData) => {
    try {
      await axios.put(`${API}/admin/niamhs-corner/schedule/${scheduleId}`, postData, { headers });
      toast.success("Scheduled post updated!");
      setEditingScheduledPost(null);
      fetchNiamhSchedule();
    } catch (error) {
      toast.error("Failed to update scheduled post");
    }
  };

  const deleteScheduledPost = async (scheduleId) => {
    if (!confirm("Delete this scheduled post?")) return;
    try {
      await axios.delete(`${API}/admin/niamhs-corner/schedule/${scheduleId}`, { headers });
      toast.success("Scheduled post deleted!");
      fetchNiamhSchedule();
    } catch (error) {
      toast.error("Failed to delete scheduled post");
    }
  };

  const publishScheduledPost = async (scheduleId) => {
    try {
      await axios.post(`${API}/admin/niamhs-corner/schedule/${scheduleId}/publish`, {}, { headers });
      toast.success("Post published!");
      fetchNiamhSchedule();
      fetchNiamhPosts();
    } catch (error) {
      toast.error("Failed to publish post");
    }
  };

  // ============== SEN SCHEDULE FUNCTIONS ==============
  const fetchSenSchedule = async () => {
    try {
      const res = await axios.get(`${API}/admin/sen/schedule`, { headers });
      setSenSchedule(res.data);
    } catch (error) {
      console.error("Error fetching SEN schedule:", error);
    }
  };

  const createSenScheduledPost = async () => {
    if (!newSenPost.title || !newSenPost.content || !newSenPost.scheduled_date) {
      toast.error("Title, content, and date are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/sen/schedule`, newSenPost, { headers });
      toast.success("SEN post scheduled!");
      setNewSenPost({
        title: "", content: "", category: "tips", scheduled_date: "", scheduled_time: "14:00",
        image_url: "", video_url: "", external_link: "", prompt_question: "",
        include_in_newsletter: true, tags: []
      });
      setShowSenScheduleForm(false);
      fetchSenSchedule();
    } catch (error) {
      toast.error("Failed to schedule SEN post");
    }
  };

  const deleteSenScheduledPost = async (scheduleId) => {
    if (!confirm("Delete this scheduled SEN post?")) return;
    try {
      await axios.delete(`${API}/admin/sen/schedule/${scheduleId}`, { headers });
      toast.success("SEN post deleted!");
      fetchSenSchedule();
    } catch (error) {
      toast.error("Failed to delete SEN post");
    }
  };

  const publishSenScheduledPost = async (scheduleId) => {
    try {
      await axios.post(`${API}/admin/sen/schedule/${scheduleId}/publish`, {}, { headers });
      toast.success("SEN post published!");
      fetchSenSchedule();
    } catch (error) {
      toast.error("Failed to publish SEN post");
    }
  };

  const fetchTickerMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/ticker/messages`, { headers });
      setTickerMessages(res.data.messages || []);
    } catch (error) {
      console.error("Error fetching ticker:", error);
    }
  };

  const fetchVenues = async () => {
    try {
      const res = await axios.get(`${API}/admin/venues`, { headers });
      setVenues(res.data);
    } catch (error) {
      console.error("Error fetching venues:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/admin/events`, { headers });
      setEvents(res.data.events || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  // NEW: Newsletter and calendar functions
  const fetchContentCalendar = async () => {
    try {
      const res = await axios.get(`${API}/admin/content-calendar`, { headers });
      setContentCalendar(res.data);
    } catch (error) {
      console.error("Error fetching calendar:", error);
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/billing/pricing`);
      setPricing(res.data);
    } catch (error) {
      console.error("Error fetching pricing:", error);
    }
  };

  const fetchAffiliateStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/affiliates`, { headers });
      setAffiliateStats(res.data);
    } catch (error) {
      console.error("Error fetching affiliate stats:", error);
    }
  };

  const generateNewsletter = async (sendNow = false) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter/generate-weekly?send_now=${sendNow}`, {}, { headers });
      toast.success(res.data.message);
      if (res.data.preview_html) {
        setNewsletterPreview(res.data.preview_html);
      }
      fetchContentCalendar();
    } catch (error) {
      toast.error("Failed to generate newsletter");
    } finally {
      setLoading(false);
    }
  };

  const scheduleWeeklyNewsletter = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter/schedule-weekly`, {}, { headers });
      toast.success(res.data.message);
      fetchContentCalendar();
    } catch (error) {
      toast.error("Failed to schedule newsletter");
    } finally {
      setLoading(false);
    }
  };

  const createCalendarItem = async () => {
    if (!newCalendarItem.title || !newCalendarItem.scheduled_date) {
      toast.error("Title and date are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/content-calendar`, newCalendarItem, { headers });
      toast.success("Calendar item created!");
      setNewCalendarItem({ title: "", content_type: "niamh_post", scheduled_date: "", content: "" });
      setShowForm(false);
      fetchContentCalendar();
    } catch (error) {
      toast.error("Failed to create calendar item");
    }
  };

  // Create functions
  const createNiamhPost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error("Title and content are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/niamhs-corner/posts`, newPost, { headers });
      toast.success("Post created!");
      setNewPost({ title: "", content: "", category: "loving", video_url: "", activity_sheet_url: "", image_url: "", prompt_question: "" });
      setShowForm(false);
      fetchNiamhPosts();
    } catch (error) {
      toast.error("Failed to create post");
    }
  };

  const createTickerMessage = async () => {
    if (!newTicker.message) {
      toast.error("Message is required");
      return;
    }
    try {
      await axios.post(`${API}/admin/ticker/messages`, newTicker, { headers });
      toast.success("Ticker message created!");
      setNewTicker({ message: "", emoji: "🌟", link: "", priority: 5 });
      setShowForm(false);
      fetchTickerMessages();
    } catch (error) {
      toast.error("Failed to create ticker");
    }
  };

  const createVenue = async () => {
    if (!newVenue.name || !newVenue.city) {
      toast.error("Name and city are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/venues`, newVenue, { headers });
      toast.success("Venue created!");
      setNewVenue({ name: "", category: "soft_play", city: "", postcode: "", website: "", email: "", description: "" });
      setShowForm(false);
      fetchVenues();
    } catch (error) {
      toast.error("Failed to create venue");
    }
  };

  const createEvent = async () => {
    if (!newEvent.name || !newEvent.date || !newEvent.city) {
      toast.error("Name, date and city are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/events`, newEvent, { headers });
      toast.success("Event created and added to ticker!");
      setNewEvent({ name: "", description: "", date: "", time: "", location: "", city: "", website: "", is_free: false });
      setShowForm(false);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to create event");
    }
  };

  // Delete functions
  const deleteNiamhPost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/admin/niamhs-corner/posts/${postId}`, { headers });
      toast.success("Post deleted");
      fetchNiamhPosts();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const deleteTickerMessage = async (msgId) => {
    if (!window.confirm("Delete this ticker message?")) return;
    try {
      await axios.delete(`${API}/admin/ticker/messages/${msgId}`, { headers });
      toast.success("Ticker deleted");
      fetchTickerMessages();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Seed functions
  const seedVenues = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/venues/seed`, {}, { headers });
      toast.success(`Seeded ${res.data.inserted} venues!`);
      fetchVenues();
    } catch (error) {
      toast.error("Failed to seed venues");
    } finally {
      setLoading(false);
    }
  };

  const seedEvents = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/events/seed`, {}, { headers });
      toast.success(`Seeded ${res.data.inserted} events!`);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to seed events");
    } finally {
      setLoading(false);
    }
  };

  // Bulk outreach
  const sendBulkOutreach = async () => {
    const notContacted = venues.venues.filter(v => v.outreach_status === "not_contacted" && v.email);
    if (notContacted.length === 0) {
      toast.error("No venues with emails to contact");
      return;
    }
    if (!window.confirm(`Send outreach emails to ${notContacted.length} venues?`)) return;
    
    setLoading(true);
    try {
      const venueIds = notContacted.map(v => v.venue_id);
      const res = await axios.post(`${API}/admin/venues/bulk-outreach`, venueIds, { headers });
      toast.success(`Sent ${res.data.sent} emails!`);
      fetchVenues();
    } catch (error) {
      toast.error("Failed to send outreach");
    } finally {
      setLoading(false);
    }
  };

  const CATEGORY_OPTIONS = [
    { value: "loving", label: "💖 What I'm Loving" },
    { value: "not_for_me", label: "😅 Not For Me" },
    { value: "activity", label: "🎨 Activity" },
    { value: "recommendation", label: "⭐ Recommendation" },
    { value: "something_new", label: "✨ Something New!" },
    { value: "liked_activity", label: "🌟 Liked Activity" }
  ];

  const VENUE_CATEGORIES = [
    "soft_play", "trampoline_park", "swimming", "farm", "zoo", 
    "museum", "adventure", "cinema", "bowling", "ice_skating", 
    "crafts", "party", "outdoor", "education", "other"
  ];

  return (
    <div className="bg-white rounded-xl p-6 border border-purple-200">
      <h2 className="text-xl font-bold text-purple-900 mb-6">Content Management</h2>
      
      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {[
          { id: "niamh", label: "Niamh's Corner", icon: Star },
          { id: "sen", label: "Kiera's SEN", icon: Heart },
          { id: "ticker", label: "Events Ticker", icon: MessageCircle },
          { id: "venues", label: "Venues", icon: MapPin },
          { id: "events", label: "Events", icon: Calendar },
          { id: "newsletter", label: "Newsletter & Calendar", icon: Newspaper },
          { id: "affiliates", label: "Affiliates", icon: Link },
          { id: "outreach", label: "Outreach Emails", icon: Send }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveSection(tab.id); setShowForm(false); setShowScheduleForm(false); setShowSenScheduleForm(false); setNewsletterPreview(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
              activeSection === tab.id
                ? "bg-purple-500 text-purple-900"
                : "bg-purple-50 text-purple-600 hover:bg-purple-100"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Niamh's Corner Section */}
      {activeSection === "niamh" && (
        <div>
          {/* View Toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setScheduleView("table")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  scheduleView === "table" ? "bg-purple-500 text-white" : "bg-purple-100 text-purple-700"
                }`}
              >
                <Table className="w-4 h-4" /> Content Schedule
              </button>
              <button
                onClick={() => setScheduleView("posts")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  scheduleView === "posts" ? "bg-purple-500 text-white" : "bg-purple-100 text-purple-700"
                }`}
              >
                <Star className="w-4 h-4" /> All Posts ({niamhPosts.length})
              </button>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setShowScheduleForm(!showScheduleForm)} size="sm" variant="outline">
                <CalendarDays className="w-4 h-4 mr-1" /> Schedule Post
              </Button>
              <Button onClick={() => setShowForm(!showForm)} size="sm">
                <Plus className="w-4 h-4 mr-1" /> Post Now
              </Button>
            </div>
          </div>

          {/* Schedule Post Form */}
          {showScheduleForm && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 mb-6 border border-purple-200">
              <h4 className="font-bold text-purple-900 mb-4 flex items-center gap-2">
                <CalendarDays className="w-5 h-5" /> Schedule a Post for Friday 2pm
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Input
                    placeholder="Post title"
                    value={newScheduledPost.title}
                    onChange={(e) => setNewScheduledPost({ ...newScheduledPost, title: e.target.value })}
                  />
                  <textarea
                    placeholder="Post content - write Niamh's message here..."
                    value={newScheduledPost.content}
                    onChange={(e) => setNewScheduledPost({ ...newScheduledPost, content: e.target.value })}
                    className="w-full p-3 bg-white border border-purple-200 rounded-lg text-purple-900"
                    rows={4}
                  />
                  <select
                    value={newScheduledPost.category}
                    onChange={(e) => setNewScheduledPost({ ...newScheduledPost, category: e.target.value })}
                    className="w-full p-2 bg-white border border-purple-200 rounded-lg text-purple-900"
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={newScheduledPost.scheduled_date}
                      onChange={(e) => setNewScheduledPost({ ...newScheduledPost, scheduled_date: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="time"
                      value={newScheduledPost.scheduled_time}
                      onChange={(e) => setNewScheduledPost({ ...newScheduledPost, scheduled_time: e.target.value })}
                      className="w-28"
                    />
                  </div>
                  <Input
                    placeholder="Image URL (paste from Media Library)"
                    value={newScheduledPost.image_url}
                    onChange={(e) => setNewScheduledPost({ ...newScheduledPost, image_url: e.target.value })}
                  />
                  <Input
                    placeholder="Prompt question for comments (optional)"
                    value={newScheduledPost.prompt_question}
                    onChange={(e) => setNewScheduledPost({ ...newScheduledPost, prompt_question: e.target.value })}
                  />
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm text-purple-700">
                      <input
                        type="checkbox"
                        checked={newScheduledPost.include_in_newsletter}
                        onChange={(e) => setNewScheduledPost({ ...newScheduledPost, include_in_newsletter: e.target.checked })}
                        className="rounded border-purple-300"
                      />
                      Include in Newsletter
                    </label>
                    <label className="flex items-center gap-2 text-sm text-purple-700">
                      <input
                        type="checkbox"
                        checked={newScheduledPost.is_sen_content}
                        onChange={(e) => setNewScheduledPost({ ...newScheduledPost, is_sen_content: e.target.checked })}
                        className="rounded border-purple-300"
                      />
                      SEN Zen Corner
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={createScheduledPost}>Schedule Post</Button>
                <Button variant="outline" onClick={() => setShowScheduleForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Content Schedule Table View */}
          {scheduleView === "table" && (
            <div className="mb-6">
              <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 border-b border-purple-200">
                  <h4 className="font-bold text-purple-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5" /> Friday Content Schedule (2pm uploads)
                  </h4>
                  <p className="text-sm text-purple-600 mt-1">Plan posts in advance • Posts go out in newsletter • SEN content goes to SEN Zen Corner</p>
                </div>
                
                {/* Schedule Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-purple-50">
                      <tr>
                        <th className="p-3 text-left font-semibold text-purple-900 border-b border-purple-200 w-28">Date</th>
                        <th className="p-3 text-left font-semibold text-pink-700 border-b border-purple-200">💖 Loving</th>
                        <th className="p-3 text-left font-semibold text-slate-700 border-b border-purple-200">😅 Not For Me</th>
                        <th className="p-3 text-left font-semibold text-emerald-700 border-b border-purple-200">✨ Something New</th>
                        <th className="p-3 text-left font-semibold text-purple-700 border-b border-purple-200">🎨 Activity</th>
                        <th className="p-3 text-left font-semibold text-amber-700 border-b border-purple-200">⭐ Recommendation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {niamhSchedule.upcoming_fridays?.slice(0, 12).map((friday, idx) => {
                        const fridayDate = new Date(friday);
                        const isToday = new Date().toDateString() === fridayDate.toDateString();
                        const isPast = fridayDate < new Date();
                        
                        // Get posts scheduled for this Friday by category
                        const getPostForCategory = (category) => {
                          return niamhSchedule.scheduled_posts?.find(
                            p => p.scheduled_date === friday && p.category === category
                          );
                        };
                        
                        const categories = ["loving", "not_for_me", "something_new", "activity", "recommendation"];
                        
                        return (
                          <tr key={friday} className={`border-b border-purple-100 ${isToday ? "bg-yellow-50" : isPast ? "bg-gray-50" : "hover:bg-purple-50"}`}>
                            <td className="p-3 font-medium text-purple-900">
                              <div className="flex flex-col">
                                <span>{fridayDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                                <span className="text-xs text-purple-500">
                                  {isToday ? "TODAY" : fridayDate.toLocaleDateString("en-GB", { weekday: "short" })}
                                </span>
                              </div>
                            </td>
                            {categories.map(cat => {
                              const post = getPostForCategory(cat);
                              return (
                                <td key={cat} className="p-2">
                                  {post ? (
                                    <div className="bg-white rounded-lg p-2 border border-purple-100 shadow-sm">
                                      <p className="font-medium text-purple-900 text-xs line-clamp-1">{post.title}</p>
                                      <p className="text-purple-600 text-xs line-clamp-1 mt-0.5">{post.content?.substring(0, 40)}...</p>
                                      <div className="flex items-center gap-1 mt-1">
                                        {post.image_url && <Image className="w-3 h-3 text-emerald-500" />}
                                        {post.include_in_newsletter && <Newspaper className="w-3 h-3 text-blue-500" />}
                                        {post.is_sen_content && <span className="text-xs">🧘</span>}
                                        <button
                                          onClick={() => publishScheduledPost(post.schedule_id)}
                                          className="ml-auto p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                                          title="Publish Now"
                                        >
                                          <Play className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() => deleteScheduledPost(post.schedule_id)}
                                          className="p-1 text-red-400 hover:bg-red-100 rounded"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setNewScheduledPost({ ...newScheduledPost, scheduled_date: friday, category: cat });
                                        setShowScheduleForm(true);
                                      }}
                                      className="w-full h-16 border-2 border-dashed border-purple-200 rounded-lg text-purple-400 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors flex items-center justify-center"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Scheduled Posts Summary */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <p className="text-emerald-800 font-bold text-lg">{niamhSchedule.scheduled_posts?.filter(p => p.status === "scheduled").length || 0}</p>
                  <p className="text-emerald-600 text-sm">Scheduled</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-blue-800 font-bold text-lg">{niamhSchedule.scheduled_posts?.filter(p => p.include_in_newsletter).length || 0}</p>
                  <p className="text-blue-600 text-sm">For Newsletter</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <p className="text-purple-800 font-bold text-lg">{niamhSchedule.scheduled_posts?.filter(p => p.is_sen_content).length || 0}</p>
                  <p className="text-purple-600 text-sm">SEN Content</p>
                </div>
                <div className="bg-pink-50 rounded-lg p-3 border border-pink-200">
                  <p className="text-pink-800 font-bold text-lg">{niamhPosts.length}</p>
                  <p className="text-pink-600 text-sm">Published</p>
                </div>
              </div>
            </div>
          )}

          {/* Posts List View */}
          {scheduleView === "posts" && (
            <>
              {showForm && (
                <div className="bg-purple-50 rounded-lg p-4 mb-4 space-y-3">
                  <Input
                    placeholder="Post title"
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  />
                  <textarea
                    placeholder="Post content"
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    className="w-full p-3 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                    rows={3}
                  />
                  <select
                    value={newPost.category}
                    onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                    className="w-full p-2 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <Input
                    placeholder="Image URL (optional)"
                    value={newPost.image_url}
                    onChange={(e) => setNewPost({ ...newPost, image_url: e.target.value })}
                  />
                  <Input
                    placeholder="Video URL (optional)"
                    value={newPost.video_url}
                    onChange={(e) => setNewPost({ ...newPost, video_url: e.target.value })}
                  />
                  <Input
                    placeholder="Activity Sheet URL (optional)"
                    value={newPost.activity_sheet_url}
                    onChange={(e) => setNewPost({ ...newPost, activity_sheet_url: e.target.value })}
                  />
                  <Input
                    placeholder="Prompt question for comments (optional)"
                    value={newPost.prompt_question}
                    onChange={(e) => setNewPost({ ...newPost, prompt_question: e.target.value })}
                  />
                  <p className="text-xs text-purple-600">Adding a prompt question enables comments on this post</p>
                  <div className="flex gap-2">
                    <Button onClick={createNiamhPost}>Create Post</Button>
                    <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {niamhPosts.map(post => (
                  <div key={post.post_id} className="bg-purple-50 rounded-lg p-3 flex justify-between items-start">
                    <div>
                      <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded mr-2">
                        {post.category}
                      </span>
                      <span className="text-purple-900 font-medium">{post.title}</span>
                      <p className="text-purple-600 text-sm mt-1 line-clamp-1">{post.content}</p>
                      {post.comments_count > 0 && (
                        <span className="text-xs text-emerald-600 ml-2">💬 {post.comments_count} comment(s)</span>
                      )}
                    </div>
                    <button onClick={() => deleteNiamhPost(post.post_id)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pending Comments Moderation */}
          {pendingComments.length > 0 && (
            <div className="mt-6 border-t border-purple-200 pt-4">
              <h4 className="text-md font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Pending Comments ({pendingComments.length})
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {pendingComments.map(comment => (
                  <div key={comment.comment_id} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-amber-900">{comment.author_name}</span>
                          <span className="text-xs text-amber-600">on: {comment.post_title}</span>
                        </div>
                        <p className="text-amber-800 text-sm">{comment.content}</p>
                        <span className="text-xs text-amber-500">{new Date(comment.created_at).toLocaleString()}</span>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button 
                          onClick={() => moderateComment(comment.comment_id, "approved")}
                          className="p-1.5 bg-emerald-100 text-emerald-600 rounded-lg hover:bg-emerald-200 transition-colors"
                          title="Approve"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => moderateComment(comment.comment_id, "rejected")}
                          className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          title="Reject"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Kiera's SEN Section */}
      {activeSection === "sen" && (
        <div>
          {/* View Toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setSenScheduleView("table")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  senScheduleView === "table" ? "bg-teal-500 text-white" : "bg-teal-100 text-teal-700"
                }`}
              >
                <Table className="w-4 h-4" /> Content Schedule
              </button>
            </div>
            <Button onClick={() => setShowSenScheduleForm(!showSenScheduleForm)} size="sm" className="bg-teal-500 hover:bg-teal-600">
              <CalendarDays className="w-4 h-4 mr-1" /> Schedule SEN Post
            </Button>
          </div>

          {/* Schedule SEN Post Form */}
          {showSenScheduleForm && (
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-5 mb-6 border border-teal-200">
              <h4 className="font-bold text-teal-900 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" /> Schedule Kiera&apos;s SEN Content
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Input
                    placeholder="Post title"
                    value={newSenPost.title}
                    onChange={(e) => setNewSenPost({ ...newSenPost, title: e.target.value })}
                  />
                  <textarea
                    placeholder="Post content - write Kiera's SEN advice/story here..."
                    value={newSenPost.content}
                    onChange={(e) => setNewSenPost({ ...newSenPost, content: e.target.value })}
                    className="w-full p-3 bg-white border border-teal-200 rounded-lg text-teal-900"
                    rows={4}
                  />
                  <select
                    value={newSenPost.category}
                    onChange={(e) => setNewSenPost({ ...newSenPost, category: e.target.value })}
                    className="w-full p-2 bg-white border border-teal-200 rounded-lg text-teal-900"
                  >
                    <option value="tips">💡 Tips & Advice</option>
                    <option value="accessible_outings">🎯 Accessible Outings</option>
                    <option value="govt_updates">📋 Govt/NHS Updates</option>
                    <option value="sensory_activities">🌈 Sensory Activities</option>
                    <option value="support_resources">🤝 Support Resources</option>
                    <option value="personal_story">💜 Kiera&apos;s Story</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={newSenPost.scheduled_date}
                      onChange={(e) => setNewSenPost({ ...newSenPost, scheduled_date: e.target.value })}
                      className="flex-1"
                    />
                    <Input
                      type="time"
                      value={newSenPost.scheduled_time}
                      onChange={(e) => setNewSenPost({ ...newSenPost, scheduled_time: e.target.value })}
                      className="w-28"
                    />
                  </div>
                  <Input
                    placeholder="Image URL (optional)"
                    value={newSenPost.image_url}
                    onChange={(e) => setNewSenPost({ ...newSenPost, image_url: e.target.value })}
                  />
                  <Input
                    placeholder="External link (e.g., NHS page, charity website)"
                    value={newSenPost.external_link}
                    onChange={(e) => setNewSenPost({ ...newSenPost, external_link: e.target.value })}
                  />
                  <Input
                    placeholder="Prompt question for engagement (optional)"
                    value={newSenPost.prompt_question}
                    onChange={(e) => setNewSenPost({ ...newSenPost, prompt_question: e.target.value })}
                  />
                  <label className="flex items-center gap-2 text-sm text-teal-700">
                    <input
                      type="checkbox"
                      checked={newSenPost.include_in_newsletter}
                      onChange={(e) => setNewSenPost({ ...newSenPost, include_in_newsletter: e.target.checked })}
                      className="rounded border-teal-300"
                    />
                    Include in Newsletter
                  </label>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={createSenScheduledPost} className="bg-teal-500 hover:bg-teal-600">Schedule SEN Post</Button>
                <Button variant="outline" onClick={() => setShowSenScheduleForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* SEN Content Schedule Table */}
          {senScheduleView === "table" && (
            <div className="mb-6">
              <div className="bg-white rounded-xl border border-teal-200 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-100 to-cyan-100 p-4 border-b border-teal-200">
                  <h4 className="font-bold text-teal-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5" /> Kiera&apos;s SEN Content Schedule (Friday 2pm)
                  </h4>
                  <p className="text-sm text-teal-600 mt-1">Tips • Accessible outings • Govt/NHS updates • Sensory activities • Support resources</p>
                </div>
                
                {/* Schedule Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-teal-50">
                      <tr>
                        <th className="p-3 text-left font-semibold text-teal-900 border-b border-teal-200 w-28">Date</th>
                        <th className="p-3 text-left font-semibold text-amber-700 border-b border-teal-200">💡 Tips</th>
                        <th className="p-3 text-left font-semibold text-emerald-700 border-b border-teal-200">🎯 Outings</th>
                        <th className="p-3 text-left font-semibold text-blue-700 border-b border-teal-200">📋 Govt/NHS</th>
                        <th className="p-3 text-left font-semibold text-purple-700 border-b border-teal-200">🌈 Sensory</th>
                        <th className="p-3 text-left font-semibold text-pink-700 border-b border-teal-200">🤝 Support</th>
                      </tr>
                    </thead>
                    <tbody>
                      {senSchedule.upcoming_fridays?.slice(0, 12).map((friday) => {
                        const fridayDate = new Date(friday);
                        const isToday = new Date().toDateString() === fridayDate.toDateString();
                        const isPast = fridayDate < new Date();
                        
                        const getPostForCategory = (category) => {
                          return senSchedule.scheduled_posts?.find(
                            p => p.scheduled_date === friday && p.category === category
                          );
                        };
                        
                        const categories = ["tips", "accessible_outings", "govt_updates", "sensory_activities", "support_resources"];
                        
                        return (
                          <tr key={friday} className={`border-b border-teal-100 ${isToday ? "bg-yellow-50" : isPast ? "bg-gray-50" : "hover:bg-teal-50"}`}>
                            <td className="p-3 font-medium text-teal-900">
                              <div className="flex flex-col">
                                <span>{fridayDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                                <span className="text-xs text-teal-500">
                                  {isToday ? "TODAY" : fridayDate.toLocaleDateString("en-GB", { weekday: "short" })}
                                </span>
                              </div>
                            </td>
                            {categories.map(cat => {
                              const post = getPostForCategory(cat);
                              return (
                                <td key={cat} className="p-2">
                                  {post ? (
                                    <div className="bg-white rounded-lg p-2 border border-teal-100 shadow-sm">
                                      <p className="font-medium text-teal-900 text-xs line-clamp-1">{post.title}</p>
                                      <p className="text-teal-600 text-xs line-clamp-1 mt-0.5">{post.content?.substring(0, 40)}...</p>
                                      <div className="flex items-center gap-1 mt-1">
                                        {post.image_url && <Image className="w-3 h-3 text-emerald-500" />}
                                        {post.include_in_newsletter && <Newspaper className="w-3 h-3 text-blue-500" />}
                                        {post.external_link && <ExternalLink className="w-3 h-3 text-violet-500" />}
                                        <button
                                          onClick={() => publishSenScheduledPost(post.schedule_id)}
                                          className="ml-auto p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                                          title="Publish Now"
                                        >
                                          <Play className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={() => deleteSenScheduledPost(post.schedule_id)}
                                          className="p-1 text-red-400 hover:bg-red-100 rounded"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setNewSenPost({ ...newSenPost, scheduled_date: friday, category: cat });
                                        setShowSenScheduleForm(true);
                                      }}
                                      className="w-full h-16 border-2 border-dashed border-teal-200 rounded-lg text-teal-400 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50 transition-colors flex items-center justify-center"
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Stats Summary */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <p className="text-emerald-800 font-bold text-lg">{senSchedule.scheduled_posts?.filter(p => p.status === "scheduled").length || 0}</p>
                  <p className="text-emerald-600 text-sm">Scheduled</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-blue-800 font-bold text-lg">{senSchedule.scheduled_posts?.filter(p => p.include_in_newsletter).length || 0}</p>
                  <p className="text-blue-600 text-sm">For Newsletter</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-amber-800 font-bold text-lg">{senSchedule.scheduled_posts?.filter(p => p.category === "tips").length || 0}</p>
                  <p className="text-amber-600 text-sm">Tips Posts</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <p className="text-purple-800 font-bold text-lg">{senSchedule.scheduled_posts?.filter(p => p.category === "govt_updates").length || 0}</p>
                  <p className="text-purple-600 text-sm">Govt Updates</p>
                </div>
              </div>

              {/* Content Ideas */}
              <div className="mt-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-5 border border-teal-200">
                <h4 className="font-bold text-teal-900 mb-3">💡 Content Ideas for Kiera&apos;s SEN Corner</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-teal-800 mb-2">💡 Tips & Advice</p>
                    <ul className="text-teal-700 space-y-1">
                      <li>• Transition tips for school holidays</li>
                      <li>• Managing sensory overload at events</li>
                      <li>• Visual schedule templates</li>
                      <li>• Calm-down strategies that work</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-teal-800 mb-2">🎯 Accessible Outings</p>
                    <ul className="text-teal-700 space-y-1">
                      <li>• Sensory-friendly cinema screenings</li>
                      <li>• Quiet hours at attractions</li>
                      <li>• Autism-friendly soft plays</li>
                      <li>• Venues with Changing Places</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-teal-800 mb-2">📋 Govt/NHS Updates</p>
                    <ul className="text-teal-700 space-y-1">
                      <li>• EHCP application timelines</li>
                      <li>• DLA/PIP updates for families</li>
                      <li>• NHS waiting list changes</li>
                      <li>• School SENCO requirements</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-teal-800 mb-2">🌈 Sensory Activities</p>
                    <ul className="text-teal-700 space-y-1">
                      <li>• DIY sensory bin ideas</li>
                      <li>• Fidget toys that actually help</li>
                      <li>• Calming bedroom setups</li>
                      <li>• Movement breaks for focus</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ticker Section */}
      {activeSection === "ticker" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-purple-900">Ticker Messages ({tickerMessages.length})</h3>
            <Button onClick={() => setShowForm(!showForm)} size="sm">
              <Plus className="w-4 h-4 mr-1" /> New Message
            </Button>
          </div>
          
          {showForm && (
            <div className="bg-purple-50 rounded-lg p-4 mb-4 space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Emoji"
                  value={newTicker.emoji}
                  onChange={(e) => setNewTicker({ ...newTicker, emoji: e.target.value })}
                  className="w-20"
                />
                <Input
                  placeholder="Message"
                  value={newTicker.message}
                  onChange={(e) => setNewTicker({ ...newTicker, message: e.target.value })}
                  className="flex-1"
                />
              </div>
              <Input
                placeholder="Link URL (optional)"
                value={newTicker.link}
                onChange={(e) => setNewTicker({ ...newTicker, link: e.target.value })}
              />
              <div className="flex gap-2">
                <Button onClick={createTickerMessage}>Add Message</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </div>
          )}
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {tickerMessages.map(msg => (
              <div key={msg.message_id} className="bg-purple-50 rounded-lg p-3 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{msg.emoji}</span>
                  <span className="text-purple-900">{msg.message}</span>
                  {msg.link && <ExternalLink className="w-3 h-3 text-purple-600" />}
                </div>
                <button onClick={() => deleteTickerMessage(msg.message_id)} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Venues Section */}
      {activeSection === "venues" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-purple-900">Venues ({venues.stats?.total || 0})</h3>
              <p className="text-sm text-purple-600">
                Partners: {venues.stats?.partners || 0} | Not Contacted: {venues.stats?.not_contacted || 0}
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={seedVenues} variant="outline" size="sm" disabled={loading}>
                Seed UK Venues
              </Button>
              <Button onClick={sendBulkOutreach} size="sm" disabled={loading}>
                <Send className="w-4 h-4 mr-1" /> Send Outreach
              </Button>
              <Button onClick={() => setShowForm(!showForm)} size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add Venue
              </Button>
            </div>
          </div>
          
          {showForm && (
            <div className="bg-purple-50 rounded-lg p-4 mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Venue name *"
                  value={newVenue.name}
                  onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
                />
                <select
                  value={newVenue.category}
                  onChange={(e) => setNewVenue({ ...newVenue, category: e.target.value })}
                  className="p-2 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                >
                  {VENUE_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat.replace("_", " ")}</option>
                  ))}
                </select>
                <Input
                  placeholder="City *"
                  value={newVenue.city}
                  onChange={(e) => setNewVenue({ ...newVenue, city: e.target.value })}
                />
                <Input
                  placeholder="Postcode"
                  value={newVenue.postcode}
                  onChange={(e) => setNewVenue({ ...newVenue, postcode: e.target.value })}
                />
                <Input
                  placeholder="Website"
                  value={newVenue.website}
                  onChange={(e) => setNewVenue({ ...newVenue, website: e.target.value })}
                />
                <Input
                  placeholder="Email (for outreach)"
                  value={newVenue.email}
                  onChange={(e) => setNewVenue({ ...newVenue, email: e.target.value })}
                />
              </div>
              <textarea
                placeholder="Description"
                value={newVenue.description}
                onChange={(e) => setNewVenue({ ...newVenue, description: e.target.value })}
                className="w-full p-3 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                rows={2}
              />
              <div className="flex gap-2">
                <Button onClick={createVenue}>Add Venue</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </div>
          )}
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {venues.venues?.slice(0, 50).map(venue => (
              <div key={venue.venue_id} className="bg-purple-50 rounded-lg p-3 flex justify-between items-center">
                <div>
                  <span className="text-purple-900 font-medium">{venue.name}</span>
                  <span className="text-purple-600 text-sm ml-2">{venue.city}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                    venue.outreach_status === "not_contacted" ? "bg-purple-200 text-purple-700" :
                    venue.outreach_status === "contacted" ? "bg-blue-500/20 text-blue-300" :
                    venue.outreach_status === "interested" ? "bg-green-500/20 text-green-300" :
                    "bg-purple-200 text-purple-700"
                  }`}>
                    {venue.outreach_status}
                  </span>
                  {venue.is_partner && (
                    <span className="ml-2 text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded">
                      Partner
                    </span>
                  )}
                </div>
                {venue.website && (
                  <a href={venue.website} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-900">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Events Section */}
      {activeSection === "events" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-purple-900">Events ({events.length})</h3>
            <div className="flex gap-2">
              <Button onClick={seedEvents} variant="outline" size="sm" disabled={loading}>
                Seed Events
              </Button>
              <Button onClick={() => setShowForm(!showForm)} size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add Event
              </Button>
            </div>
          </div>
          
          {showForm && (
            <div className="bg-purple-50 rounded-lg p-4 mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Event name *"
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                />
                <Input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                />
                <Input
                  placeholder="Location"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                />
                <Input
                  placeholder="City *"
                  value={newEvent.city}
                  onChange={(e) => setNewEvent({ ...newEvent, city: e.target.value })}
                />
                <Input
                  placeholder="Time (e.g. 10:00 - 18:00)"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                />
                <Input
                  placeholder="Website"
                  value={newEvent.website}
                  onChange={(e) => setNewEvent({ ...newEvent, website: e.target.value })}
                />
              </div>
              <textarea
                placeholder="Description"
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                className="w-full p-3 bg-purple-100 border border-purple-200 rounded-lg text-purple-900"
                rows={2}
              />
              <label className="flex items-center gap-2 text-purple-900">
                <input
                  type="checkbox"
                  checked={newEvent.is_free}
                  onChange={(e) => setNewEvent({ ...newEvent, is_free: e.target.checked })}
                />
                Free Event
              </label>
              <div className="flex gap-2">
                <Button onClick={createEvent}>Add Event</Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </div>
          )}
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {events.map(event => (
              <div key={event.event_id} className="bg-purple-50 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-purple-900 font-medium">{event.name}</span>
                    <span className="text-purple-600 text-sm ml-2">{event.city}</span>
                    {event.is_free && (
                      <span className="ml-2 text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">
                        Free
                      </span>
                    )}
                  </div>
                  <span className="text-purple-400 text-sm">{event.date}</span>
                </div>
                <p className="text-purple-600 text-sm mt-1 line-clamp-1">{event.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Newsletter & Calendar Section */}
      {activeSection === "newsletter" && (
        <div>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Automated Newsletter */}
            <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-xl p-6 border border-purple-500/30">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-8 h-8 text-yellow-400" />
                <div>
                  <h3 className="text-lg font-bold text-purple-900">AI Weekly Newsletter</h3>
                  <p className="text-sm text-purple-600">This Week at KiddiPlay</p>
                </div>
              </div>
              <p className="text-purple-700 text-sm mb-4">
                Auto-generates content from Niamhs posts, events, weather tips, and family challenges.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => generateNewsletter(false)} disabled={loading} size="sm">
                  <Eye className="w-4 h-4 mr-1" /> Preview
                </Button>
                <Button onClick={() => generateNewsletter(true)} disabled={loading} size="sm" variant="outline">
                  <Send className="w-4 h-4 mr-1" /> Generate & Send
                </Button>
                <Button onClick={scheduleWeeklyNewsletter} disabled={loading} size="sm" variant="outline">
                  <Clock className="w-4 h-4 mr-1" /> Schedule Weekly
                </Button>
              </div>
            </div>

            {/* Scheduled Newsletters */}
            <div className="bg-purple-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-purple-900 mb-4">Scheduled Newsletters</h3>
              {contentCalendar.scheduled_newsletters?.length > 0 ? (
                <div className="space-y-3">
                  {contentCalendar.scheduled_newsletters.map(sched => (
                    <div key={sched.schedule_id} className="bg-purple-100 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-purple-900 font-medium">{sched.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${sched.status === 'scheduled' ? 'bg-green-500/20 text-green-300' : 'bg-purple-200 text-purple-700'}`}>
                          {sched.status}
                        </span>
                      </div>
                      <p className="text-sm text-purple-600 mt-1">
                        Next: {new Date(sched.next_send).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-purple-600 text-sm">No newsletters scheduled. Click Schedule Weekly to set up automatic Friday sends.</p>
              )}
            </div>
          </div>

          {/* Newsletter Preview */}
          {newsletterPreview && (
            <div className="bg-purple-50 rounded-xl p-4 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-purple-900">Newsletter Preview</h3>
                <Button onClick={() => setNewsletterPreview(null)} variant="ghost" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="bg-white rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <div dangerouslySetInnerHTML={{ __html: newsletterPreview }} />
              </div>
            </div>
          )}

          {/* Content Calendar */}
          <div className="bg-purple-50 rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-purple-900">Content Calendar</h3>
              <Button onClick={() => setShowForm(!showForm)} size="sm">
                <Plus className="w-4 h-4 mr-1" /> Plan Content
              </Button>
            </div>
            
            {showForm && (
              <div className="bg-purple-100 rounded-lg p-4 mb-4 space-y-3">
                <Input
                  placeholder="Content title"
                  value={newCalendarItem.title}
                  onChange={(e) => setNewCalendarItem({ ...newCalendarItem, title: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={newCalendarItem.content_type}
                    onChange={(e) => setNewCalendarItem({ ...newCalendarItem, content_type: e.target.value })}
                    className="p-2 bg-purple-200 border border-purple-300 rounded-lg text-purple-900"
                  >
                    <option value="niamh_post">Niamhs Post</option>
                    <option value="ticker">Ticker Message</option>
                    <option value="newsletter">Newsletter</option>
                    <option value="social">Social Media</option>
                  </select>
                  <Input
                    type="date"
                    value={newCalendarItem.scheduled_date}
                    onChange={(e) => setNewCalendarItem({ ...newCalendarItem, scheduled_date: e.target.value })}
                  />
                </div>
                <textarea
                  placeholder="Notes / Content draft"
                  value={newCalendarItem.content}
                  onChange={(e) => setNewCalendarItem({ ...newCalendarItem, content: e.target.value })}
                  className="w-full p-3 bg-purple-200 border border-purple-300 rounded-lg text-purple-900"
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button onClick={createCalendarItem}>Add to Calendar</Button>
                  <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {contentCalendar.calendar_items?.length > 0 ? (
                contentCalendar.calendar_items.map(item => (
                  <div key={item.item_id} className="bg-purple-100 rounded-lg p-3 flex justify-between items-center">
                    <div>
                      <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded mr-2">
                        {item.content_type}
                      </span>
                      <span className="text-purple-900">{item.title}</span>
                    </div>
                    <span className="text-purple-600 text-sm">{item.scheduled_date}</span>
                  </div>
                ))
              ) : (
                <p className="text-purple-600 text-sm text-center py-4">No content planned yet. Start planning your content!</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Affiliates Section */}
      {activeSection === "affiliates" && (
        <div>
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-900/50 to-teal-900/50 rounded-xl p-6 border border-green-500/30">
              <DollarSign className="w-8 h-8 text-green-400 mb-2" />
              <p className="text-3xl font-bold text-purple-900">{affiliateStats?.total_clicks || 0}</p>
              <p className="text-purple-600 text-sm">Total Affiliate Clicks</p>
            </div>
            <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-xl p-6 border border-blue-500/30">
              <CalendarDays className="w-8 h-8 text-blue-400 mb-2" />
              <p className="text-3xl font-bold text-purple-900">{affiliateStats?.monthly_clicks || 0}</p>
              <p className="text-purple-600 text-sm">This Month</p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 rounded-xl p-6 border border-purple-500/30">
              <Link className="w-8 h-8 text-purple-400 mb-2" />
              <p className="text-3xl font-bold text-purple-900">{affiliateStats?.top_venues?.length || 0}</p>
              <p className="text-purple-600 text-sm">Active Affiliate Venues</p>
            </div>
          </div>

          {/* Affiliate Programs Info */}
          <div className="bg-purple-50 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-bold text-purple-900 mb-4">Affiliate Programs to Join</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-purple-100 rounded-lg p-4">
                <h4 className="text-purple-900 font-medium">Tiqets</h4>
                <p className="text-sm text-purple-600 mb-2">Theme parks, attractions</p>
                <p className="text-green-400 font-bold">5-8% commission</p>
                <a href="https://www.tiqets.com/affiliates" target="_blank" rel="noopener noreferrer" className="text-purple-400 text-sm hover:underline">Join program →</a>
              </div>
              <div className="bg-purple-100 rounded-lg p-4">
                <h4 className="text-purple-900 font-medium">GetYourGuide</h4>
                <p className="text-sm text-purple-600 mb-2">Tours & activities</p>
                <p className="text-green-400 font-bold">8% commission</p>
                <a href="https://www.getyourguide.com/affiliate" target="_blank" rel="noopener noreferrer" className="text-purple-400 text-sm hover:underline">Join program →</a>
              </div>
              <div className="bg-purple-100 rounded-lg p-4">
                <h4 className="text-purple-900 font-medium">Amazon Associates</h4>
                <p className="text-sm text-purple-600 mb-2">Books, toys, products</p>
                <p className="text-green-400 font-bold">1-10% commission</p>
                <a href="https://affiliate-program.amazon.co.uk/" target="_blank" rel="noopener noreferrer" className="text-purple-400 text-sm hover:underline">Join program →</a>
              </div>
            </div>
          </div>

          {/* Top Performing Venues */}
          <div className="bg-purple-50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-purple-900 mb-4">Top Performing Venues</h3>
            {affiliateStats?.top_venues?.length > 0 ? (
              <div className="space-y-2">
                {affiliateStats.top_venues.map((venue, idx) => (
                  <div key={venue._id} className="bg-purple-100 rounded-lg p-3 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-purple-400">#{idx + 1}</span>
                      <span className="text-purple-900">{venue.venue_name}</span>
                    </div>
                    <span className="text-green-400 font-medium">{venue.clicks} clicks</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-purple-600 text-sm text-center py-4">No affiliate clicks yet. Add affiliate links to venues to start tracking!</p>
            )}
          </div>
        </div>
      )}

      {/* Outreach Emails Section */}
      {activeSection === "outreach" && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-purple-900 mb-4">Partnership Outreach Templates</h3>
          <p className="text-purple-600 text-sm mb-6">Pre-drafted emails for partnership outreach. Copy and customize before sending.</p>
          
          {/* Sitter Agencies */}
          <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-pink-500" />
              </div>
              <div>
                <h4 className="font-bold text-purple-900">Babysitting & Childcare Agencies</h4>
                <p className="text-xs text-purple-600">Sitters.co.uk, Bubble, Childcare.co.uk, Koru Kids, etc.</p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-purple-900 whitespace-pre-line font-mono">{`Subject: Partnership Opportunity – KiddiPlay Club x [Agency Name]

Dear [Agency Name] Team,

I hope this email finds you well. My name is Niamh, and I'm the founder of KiddiPlay Club – a UK-based family matching platform that connects families for playdates, activities, and mutual support.

We're building a "Buddy UP!" feature that enables matched families to team up and share childcare costs. Rather than recommending a single provider, we want to offer families a curated list of trusted, DBS-checked babysitting services.

**Why partner with us?**
• Direct referrals from engaged UK families actively seeking childcare
• Featured listing in our "Preferred Sitters" directory
• Potential commission-based or affiliate arrangement
• Growing community of 1000+ families (launch July 2026)

**What we're looking for:**
• A partnership agreement for referral tracking
• Information about your DBS verification process
• Pricing/commission structure for referrals

We'd love to discuss how we can work together. Would you be available for a quick call this week?

Best regards,
Niamh McCarthy
Founder, KiddiPlay Club
team@kiddiplayclub.com
www.kiddiplayclub.com`}</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-purple-200 text-purple-700 hover:bg-purple-50"
                onClick={() => {
                  navigator.clipboard.writeText(`Subject: Partnership Opportunity – KiddiPlay Club x [Agency Name]\n\nDear [Agency Name] Team,\n\nI hope this email finds you well. My name is Niamh, and I'm the founder of KiddiPlay Club – a UK-based family matching platform that connects families for playdates, activities, and mutual support.\n\nWe're building a "Buddy UP!" feature that enables matched families to team up and share childcare costs. Rather than recommending a single provider, we want to offer families a curated list of trusted, DBS-checked babysitting services.\n\n**Why partner with us?**\n• Direct referrals from engaged UK families actively seeking childcare\n• Featured listing in our "Preferred Sitters" directory\n• Potential commission-based or affiliate arrangement\n• Growing community of 1000+ families (launch July 2026)\n\n**What we're looking for:**\n• A partnership agreement for referral tracking\n• Information about your DBS verification process\n• Pricing/commission structure for referrals\n\nWe'd love to discuss how we can work together. Would you be available for a quick call this week?\n\nBest regards,\nNiamh McCarthy\nFounder, KiddiPlay Club\nteam@kiddiplayclub.com\nwww.kiddiplayclub.com`);
                  toast.success("Email copied to clipboard!");
                }}
              >
                Copy Email
              </Button>
            </div>
            <div className="mt-4 p-3 bg-amber-50 rounded-lg">
              <p className="text-xs text-amber-800 font-medium mb-1">📋 Agencies to contact:</p>
              <p className="text-xs text-amber-700">Sitters.co.uk • Bubble • Childcare.co.uk • Koru Kids • MyNanny • Care.com UK • Tinies • Eden Nannies</p>
            </div>
          </div>

          {/* Changing Places */}
          <div className="bg-white rounded-xl p-6 border border-purple-100 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-teal-500" />
              </div>
              <div>
                <h4 className="font-bold text-purple-900">Changing Places Consortium</h4>
                <p className="text-xs text-purple-600">Official partnership for accessibility data</p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-purple-900 whitespace-pre-line font-mono">{`Subject: Partnership Enquiry – KiddiPlay Club & Changing Places

Dear Changing Places Team,

I hope this email finds you well. My name is Niamh McCarthy, founder of KiddiPlay Club – a UK-based platform helping families with children (including those with additional needs) connect for playdates and activities.

**Our Mission:**
We're committed to making family activities accessible to everyone. A core feature of our platform is helping families find venues with Changing Places toilets – an essential resource for families caring for children and adults with disabilities.

**Current Implementation:**
We've already integrated a Changing Places finder into our app using publicly available data, covering 280+ locations across the UK. However, we'd love to establish an official partnership to:

• Ensure our data is accurate and up-to-date
• Display the official Changing Places logo with your blessing
• Drive awareness and advocacy for more Changing Places installations
• Provide feedback from families about facility conditions

**Our Reach:**
• UK-wide platform launching July 2026
• Strong focus on SEN families (autism, ADHD, physical disabilities)
• Dedicated SEN Corner with accessibility filters
• Growing community of engaged UK families

We believe in your mission and would be honoured to support it through our platform. Could we arrange a call to discuss a potential partnership?

Thank you for the incredible work you do.

Warm regards,
Niamh McCarthy
Founder, KiddiPlay Club
team@kiddiplayclub.com
www.kiddiplayclub.com`}</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-purple-200 text-purple-700 hover:bg-purple-50"
                onClick={() => {
                  navigator.clipboard.writeText(`Subject: Partnership Enquiry – KiddiPlay Club & Changing Places\n\nDear Changing Places Team,\n\nI hope this email finds you well. My name is Niamh McCarthy, founder of KiddiPlay Club – a UK-based platform helping families with children (including those with additional needs) connect for playdates and activities.\n\n**Our Mission:**\nWe're committed to making family activities accessible to everyone. A core feature of our platform is helping families find venues with Changing Places toilets – an essential resource for families caring for children and adults with disabilities.\n\n**Current Implementation:**\nWe've already integrated a Changing Places finder into our app using publicly available data, covering 280+ locations across the UK. However, we'd love to establish an official partnership to:\n\n• Ensure our data is accurate and up-to-date\n• Display the official Changing Places logo with your blessing\n• Drive awareness and advocacy for more Changing Places installations\n• Provide feedback from families about facility conditions\n\n**Our Reach:**\n• UK-wide platform launching July 2026\n• Strong focus on SEN families (autism, ADHD, physical disabilities)\n• Dedicated SEN Corner with accessibility filters\n• Growing community of engaged UK families\n\nWe believe in your mission and would be honoured to support it through our platform. Could we arrange a call to discuss a potential partnership?\n\nThank you for the incredible work you do.\n\nWarm regards,\nNiamh McCarthy\nFounder, KiddiPlay Club\nteam@kiddiplayclub.com\nwww.kiddiplayclub.com`);
                  toast.success("Email copied to clipboard!");
                }}
              >
                Copy Email
              </Button>
              <a 
                href="https://www.changing-places.org/contact" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-teal-500 hover:bg-teal-600">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit Website
                </Button>
              </a>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl p-6 border border-purple-200">
            <h4 className="font-bold text-purple-900 mb-3">📅 Add to Content Planner</h4>
            <p className="text-sm text-purple-700 mb-4">Don&apos;t forget to schedule these outreach emails in your content calendar!</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-purple-600 font-medium">Sitter Agencies</p>
                <p className="text-sm text-purple-900">Priority: High</p>
                <p className="text-xs text-purple-500">Deadline: Before launch</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-purple-600 font-medium">Changing Places</p>
                <p className="text-sm text-purple-900">Priority: Medium</p>
                <p className="text-xs text-purple-500">Deadline: Q3 2026</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentManager;
